import React, { useRef } from "react";
import { Label } from "@/components/ui/label";

const TimeInput = ({
  label,
  value,
  onChange,
  placeholder = "Select time",
  className = "",
  required = false,
  disabled = false,
  allowClear = true,
  step = 60, // Keep for backwards compatibility
  minuteStep = null, // New prop: if set, use custom dropdowns with this minute interval
  isInvalid = false,
  onFocus,
  ...props
}) => {
  const timeInputRef = useRef(null);

  // If minuteStep is provided, use custom dropdowns
  if (minuteStep) {
    // Parse the current value
    const [hours, minutes] = value ? value.split(":") : ["", ""];

    // Generate hour options (00-23)
    const hourOptions = Array.from({ length: 24 }, (_, i) =>
      i.toString().padStart(2, "0")
    );

    // Generate minute options based on minuteStep
    const minuteOptions = [];
    for (let i = 0; i < 60; i += minuteStep) {
      minuteOptions.push(i.toString().padStart(2, "0"));
    }

    const handleHourChange = (e) => {
      const newHour = e.target.value;
      const currentMinutes = minutes || "00";
      const newValue = newHour ? `${newHour}:${currentMinutes || "00"}` : "";

      if (onChange) {
        onChange({ target: { value: newValue } });
      }
    };

    const handleMinuteChange = (e) => {
      const newMinute = e.target.value;
      const currentHours = hours || "00";
      const newValue =
        currentHours && newMinute ? `${currentHours}:${newMinute}` : "";

      if (onChange) {
        onChange({ target: { value: newValue } });
      }
    };

    const handleClear = (e) => {
      e.stopPropagation();
      if (onChange) {
        onChange({ target: { value: "" } });
      }
    };

    return (
      <div className={`w-full ${className}`}>
        {label && (
          <Label className="block font-inter text-sm font-medium leading-5 text-ink-muted">
            {label}
            {required && <span className="ml-1 text-red-500">*</span>}
          </Label>
        )}
        <div className="relative flex w-1/2 items-center gap-2">
          {/* Hour Select */}
          <select
            value={hours}
            onChange={handleHourChange}
            onFocus={onFocus}
            disabled={disabled}
            className={`h-7 flex-1 cursor-pointer appearance-none rounded-md border bg-white px-2 py-0 text-sm text-ink leading-7 focus:outline-none focus:ring-2 focus:ring-sage focus:border-sage ${
              isInvalid ? "border-red-500" : "border-edge-input"
            } ${
              disabled ? "cursor-not-allowed bg-paper-inset text-ink-muted" : ""
            }`}
            style={{
              backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%239A9285' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
              backgroundPosition: "right 0.25rem center",
              backgroundRepeat: "no-repeat",
              backgroundSize: "1.5em 1.5em",
              paddingRight: "2rem",
            }}
          >
            <option value="" className="text-ink-muted">HH</option>
            {hourOptions.map((hour) => (
              <option key={hour} value={hour}>
                {hour}
              </option>
            ))}
          </select>

          <span className="text-sm font-medium text-ink-secondary">:</span>

          {/* Minute Select */}
          <select
            value={minutes}
            onChange={handleMinuteChange}
            onFocus={onFocus}
            disabled={disabled}
            className={`h-7 flex-1 cursor-pointer appearance-none rounded-md border bg-white px-2 py-0 text-sm text-ink leading-7 focus:outline-none focus:ring-2 focus:ring-sage focus:border-sage ${
              isInvalid ? "border-red-500" : "border-edge-input"
            } ${
              disabled ? "cursor-not-allowed bg-paper-inset text-ink-muted" : ""
            }`}
            style={{
              backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%239A9285' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
              backgroundPosition: "right 0.25rem center",
              backgroundRepeat: "no-repeat",
              backgroundSize: "1.5em 1.5em",
              paddingRight: "2rem",
            }}
          >
            <option value="" className="text-ink-muted">MM</option>
            {minuteOptions.map((minute) => (
              <option key={minute} value={minute}>
                {minute}
              </option>
            ))}
          </select>

          {allowClear && value && !disabled && (
            <button
              onClick={handleClear}
              className="flex h-7 items-center justify-center px-1 text-ink-muted transition-colors hover:text-ink-secondary"
              title="Clear Time"
              type="button"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M18 6 6 18" />
                <path d="m6 6 12 12" />
              </svg>
            </button>
          )}
        </div>
      </div>
    );
  }

  // Default behavior: use native time input
  const handleContainerClick = () => {
    if (timeInputRef.current && !disabled) {
      timeInputRef.current.focus();
      // Trigger the time picker
      timeInputRef.current.showPicker?.();
    }
  };

  const handleKeyDown = (e) => {
    if ((e.key === "Enter" || e.key === " ") && !disabled) {
      e.preventDefault();
      handleContainerClick();
    }
  };

  const handleInputClick = (e) => {
    e.stopPropagation();
    if (timeInputRef.current && !disabled) {
      timeInputRef.current.showPicker?.();
    }
  };

  const handleClear = (e) => {
    e.stopPropagation();
    if (onChange) {
      // Create a synthetic event to match the expected signature
      const syntheticEvent = {
        target: { value: "" },
      };
      onChange(syntheticEvent);
    }
  };

  return (
    <div className={`w-full ${className}`}>
      {label && (
        <Label className="block font-inter text-sm font-medium leading-5 text-ink-muted">
          {label}
          {required && <span className="ml-1 text-red-500">*</span>}
        </Label>
      )}
      <div
        className={`relative flex w-full cursor-pointer items-center rounded-md border bg-white ${
          isInvalid ? "border-red-500" : "border-edge-input"
        }`}
        onClick={handleContainerClick}
        onKeyDown={handleKeyDown}
        onFocus={onFocus}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-label={`${label || "Time"} input`}
      >
        <input
          ref={timeInputRef}
          type="time"
          value={value}
          onChange={onChange}
          onClick={handleInputClick}
          disabled={disabled}
          step={step}
            className="h-7 w-full cursor-pointer rounded-md border-0 bg-transparent px-2 text-sm text-ink focus:outline-none focus:ring-0"
          style={{
            colorScheme: "light",
          }}
          {...props}
        />
        {allowClear && value && !disabled && (
          <button
            onClick={handleClear}
            className="flex items-center justify-center p-1 text-ink-muted transition-colors hover:text-ink-secondary"
            title="Clear Time"
            type="button"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M18 6 6 18" />
              <path d="m6 6 12 12" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
};

export default TimeInput;
